import {
    ICategory,
    ICharsValuesCloth,
    ITag
} from "./../../../../../types/index";
import s from './tagsBlock.module.css';
import React, {useEffect, useState} from "react";
import {ReactComponent as DeleteIcon} from '../../../../../assets/icons/trash.svg';
import {ReactComponent as ReturnIcon} from '../../../../../assets/icons/return.svg';
import {toast} from "react-toastify";
import {appAxiosInstance, clothesAxiosInstance} from "../../../../../api";
import {Button, Form, Modal} from "react-bootstrap";
import { MaterialSelector } from './MaterialSelector/materialSelector';
import {ReactComponent as CopyIcon} from '../../../../../assets/icons/copy-icon.svg';
import { Loader } from "../../../../../components/Loader/loader";

interface ITagsBlockProps {
    clothId: string;
    isNew?: boolean;
    onAddAction?: (action: () => Promise<void>) => Promise<void>;
    hasRequiredFields?: () => boolean;
}

interface CharsCloth {
    charId: number;
    charValueId: number | null;
    charValueText: string;
    charName: string;
}

const LAYERS = [
    { name: "ABOVE_THE_WAIST_FIRST_LAYER", localizedName: "Плечевой первый слой" },
    { name: "ABOVE_THE_WAIST_SECOND_LAYER", localizedName: "Плечевой второй слой" },
    { name: "OUTERWEAR", localizedName: "Плечевые верхняя одежда" },
    { name: "PANTS", localizedName: "Поясные" },
    { name: "SHOES", localizedName: "Обувь" },
    { name: "BAG", localizedName: "Сумка" },
    { name: "ACCESSORIES", localizedName: "Аксессуары" }
];

const MODAL_DROPDOWN_ID = -1;
const EDIT_MODAL_DROPDOWN_ID = -2;
const MATERIAL_MODAL_DROPDOWN_ID = -3;
const MATERIAL_EDIT_MODAL_DROPDOWN_ID = -4;
const LAYER_MODAL_DROPDOWN_ID = -5;

export function TagsBlock({clothId, isNew, onAddAction, hasRequiredFields}: ITagsBlockProps) {
    const [nameNewTag, setNameNewTag] = useState('');
    const [valueNewTag, setValueNewTag] = useState('');
    const [requiredAddNewTag, setRequiredAddNewTag] = useState(false);
    const [isTagsLoading, setIsTagsLoading] = useState(false);
    const [charsShowingList, setCharsShowingList] = useState<Array<ICharsValuesCloth>>([]);
    const [referenceTagsValues, setReferenceTagsValues] = useState<Array<ITag>>([]);
    const [errorTagCode, setErrorTagCode] = useState<boolean>(false);
    const [requiredEditTag, setRequiredEditTag] = useState(false);
    const [editingTag, setEditingTag] = useState<ICharsValuesCloth | null>(null);
    const [lastChangedTag, setLastChangedTag] = useState<{
        charId: number | null;
        previousValue: string;
        currentValue: string;
    } | null>(null);
    const [dropdownOpen, setDropdownOpen] = useState<{ [key: number]: boolean }>({});
    const [layerDropdownOpen, setLayerDropdownOpen] = useState<{ [key: number]: boolean }>({});
    const [material, setMaterial] = useState<string[]>([]);
    const [materialModalOpen, setMaterialModalOpen] = useState(false);
    const [currentMaterialTag, setCurrentMaterialTag] = useState<ICharsValuesCloth | null>(null);
    const [materialSelectorKey, setMaterialSelectorKey] = useState(0);
    const [pollingInterval, setPollingInterval] = useState<NodeJS.Timeout | null>(null);
    const [localSelectedMaterials, setLocalSelectedMaterials] = useState<{
        [charId: number]: string[];
    }>({});

    const [selectedLayers, setSelectedLayers] = useState<string[]>([]);
    const [isLayerDropdownOpen, setIsLayerDropdownOpen] = useState(false);

    const getMaterials = async () => {
        try {
            const response = await clothesAxiosInstance.get<string[]>('characteristics/getMaterials');
            if (response.data.length) {
                setMaterial(response.data);
            } else {
                setMaterial([]);
            }
        } catch (error) {
            toast.error('Ошибка при получении материалов');
            setMaterial([]);
        }
    }

    const toggleLayerDropdown = (charId: number, e?: React.MouseEvent) => {
        if (e) {
            e.stopPropagation();
        }
        setLayerDropdownOpen(prev => ({
            ...prev,
            [charId]: !prev[charId]
        }));
    };

    const getSelectedLayerValues = (charValue: ICharsValuesCloth): string[] => {
        if (!charValue.charValueText) return [];
        return charValue.charValueText.split(',').map(l => l.trim()).filter(l => l.length > 0);
    };

    const handleLayerChange = (charValue: ICharsValuesCloth, layerName: string, e?: React.MouseEvent) => {
        if (e) {
            e.stopPropagation();
        }

        const currentValues = getSelectedLayerValues(charValue);
        let newValues: string[];

        if (currentValues.includes(layerName)) {
            newValues = currentValues.filter(value => value !== layerName);
        } else {
            newValues = [...currentValues, layerName];
        }

        const newValue = newValues.join(', ');

        setCharsShowingList(prev =>
            prev.map(tag =>
                tag.charId === charValue.charId
                    ? { ...tag, charValueText: newValue }
                    : tag
            )
        );

        handleSpecialTagChange(charValue, newValue);
    };

    const createMaterial = async (materialName: string) => {
        try {
            await appAxiosInstance.post('clothes/characteristics/createOrUpdateMaterial', materialName, {
                headers: {
                    'Content-Type': 'text/plain'
                }
            });
            await getMaterials();
        } catch (error) {
            toast.error('Ошибка при создании материала');
        }
    }

    const copyTagValue = (value:string) => {
        navigator.clipboard.writeText(value)
            .then(() => {
                toast.success('Значение тега скопировано');
            })
            .catch(err => {
                toast.error('Не удалось скопировать значение тега');
            });
    };

    const style: string[] = ['свободный', 'полуприлегающий', 'прилегающий', 'oversize'];
    const texture: string[] = ['матовая', 'матовый блеск', 'глянцевая', 'полупрозрачная', 'гладкая', 'зернистая'];
    const colorType: string[] = ['BSC', 'BBR', 'LTS', 'ACT'];
    const cut: string[] = ['прямой', 'А-силуэт', 'трапеция', 'зауженный', 'приталенный', 'однобортный', 'двубортный', 'straight cut',
        'slim fit', 'tapered cut', 'relaxed fit', 'baggy fit', 'loose fit', 'boot cut', 'flare cut',
        'barrel leg', 'wide leg'];
    const notUserDefined = ["элемент", "фасон","крой","материал","детали", "фактура", "цвет","принт","текстура",
        "тип цвета", "тип", "rgb цвет","id комплектной вещи"];

    const normalizeMultiValue = (value: string): string => {
        if (!value) return '';

        const parts = value.split(/[,\n]/);

        const normalizedParts = parts
            .map(part => part.trim())
            .filter(part => part.length > 0)
            .filter((part, index, array) => array.indexOf(part) === index);

        return normalizedParts.join(', ');
    };

    const getLayerLocalizedName = (layerName: string) => {
        const layer = LAYERS.find(l => l.name === layerName);
        return layer ? layer.localizedName : layerName;
    };

    const saveLayerTag = async (layerValues: string) => {
        try {
            const layerTag: CharsCloth = {
                charId: generateIdNewTag(),
                charValueId: null,
                charName: 'слой одежды',
                charValueText: layerValues
            };

            const response = await clothesAxiosInstance.post<boolean>(
                `editTag/${clothId}`,
                layerTag
            );

            if (response.data) {
                toast.success('Слой одежды успешно добавлен');
                await getCharsValues();
                setSelectedLayers([]);
            } else {
                toast.error('Ошибка при сохранении слоя одежды');
            }
        } catch (error) {
            toast.error('Ошибка при сохранении слоя одежды');
        }
    };

    const startSegmentation = async () => {
        try {
            setIsTagsLoading(true);
            await clothesAxiosInstance.post(`characteristics/segmentationCloth/${clothId}`);

            toast.success('Сегментация запущена');

            if (pollingInterval) stopPolling();

            const interval = setInterval(() => {
                getCharsValues();
            }, 5000);

            setPollingInterval(interval);

        } catch (error) {
            toast.error('Ошибка при запуске сегментации');
        } finally {
            setIsTagsLoading(false);
        }
    };

    const approvedSegmentation = async () => {
        try {
            await clothesAxiosInstance.post(`approvedSegmentation/${clothId}`);

            toast.success('Сегментация подтверждена');

            await getCharsValues();

        } catch (error) {
            toast.error('Ошибка при запуске сегментации');
        }
    }

    const parseMultiValue = (value: string): string[] => {
        if (!value) return [];
        return value.split(',').map(part => part.trim()).filter(part => part.length > 0);
    };

    const sortTagsByPredefinedOrder = (tags: ICharsValuesCloth[]): ICharsValuesCloth[] => {
        return [...tags].sort((a, b) => {
            const indexA = notUserDefined.findIndex(name =>
                name.toLowerCase() === a.charName.toLowerCase()
            );
            const indexB = notUserDefined.findIndex(name =>
                name.toLowerCase() === b.charName.toLowerCase()
            );

            if (indexA !== -1 && indexB !== -1) {
                return indexA - indexB;
            }

            if (indexA !== -1) {
                return -1;
            }

            if (indexB !== -1) {
                return 1;
            }

            return 0;
        });
    };

    const closeEditTag = () => {
        setRequiredEditTag(false);
        setEditingTag(null);
    }

    const showModalEditTagValue = (charValue: ICharsValuesCloth) => {
        setEditingTag(charValue);
        setRequiredEditTag(true);
    }

    const getCharsValues = async () => {
        try {
            setIsTagsLoading(true);

            const response = await clothesAxiosInstance.get<ICharsValuesCloth[]>(`charValues/${clothId}`);
            const charsValuesList = response.data || [];

            const normalizedCharsValues = charsValuesList.map(charValue => {
                if ((charValue.charName.toLowerCase() === 'крой' || charValue.charName.toLowerCase() === 'материал') && charValue.charValueText) {
                    return {
                        ...charValue,
                        charValueText: normalizeMultiValue(charValue.charValueText)
                    };
                }
                return charValue;
            });

            const sortedTags = sortTagsByPredefinedOrder(normalizedCharsValues);
            setCharsShowingList(sortedTags);

            const responseTags = await appAxiosInstance.get<ITag[]>('backend/tags/');
            if (responseTags.data && responseTags.data.length) {
                setReferenceTagsValues(responseTags.data);
            } else {
                setReferenceTagsValues([]);
                setErrorTagCode(true);
            }
        } catch (error) {
            if (errorTagCode) {
                toast.error('Ошибка при получении характеристик');
            } else {
                toast.error('Ошибка при получении общего списка тегов');
            }
        } finally {
            setIsTagsLoading(false);
        }
    }

    const generateIdNewTag = () => {
        return Math.floor(Math.random() * -8) - 50;
    }

    const canUndoTag = (charId: number) => {
        return lastChangedTag && lastChangedTag.charId === charId;
    };

    const showModalAddNewTag = () => {
        setRequiredAddNewTag(true);
    }

    const closeModalNewTag = () => {
        setRequiredAddNewTag(false);
        setNameNewTag('');
        setValueNewTag('');
    }

    const saveLastChange = (charId: number, previousValue: string, currentValue: string) => {
        if (previousValue === currentValue) return;

        setLastChangedTag({
            charId,
            previousValue,
            currentValue
        });
    };

    const isUserDefinedTag = (charName: string): boolean => {
        return !notUserDefined.some(definedName =>
            definedName.toLowerCase() === charName.toLowerCase()
        );
    };

    const isValidTag = (charValue: ICharsValuesCloth): boolean => {
        if (isUserDefinedTag(charValue.charName)) {
            return true;
        }

        const matchingTag = referenceTagsValues.find(t =>
            t.charName.toLowerCase() === charValue.charName.toLowerCase()
        );

        if (matchingTag) {
            if ((charValue.charName.toLowerCase() === 'крой' || charValue.charName.toLowerCase() === 'материал') && charValue.charValueText) {
                const values = parseMultiValue(charValue.charValueText);
                return values.every(value =>
                    matchingTag.charValues.some(correctValue =>
                        correctValue.trim().toLowerCase() === value.trim().toLowerCase()
                    )
                );
            }

            return matchingTag.charValues.some(correctValue =>
                correctValue.trim().toLowerCase() === (charValue.charValueText || '').trim().toLowerCase()
            );
        }
        return false;
    };

    const isSpecialTag = (charName: string): boolean => {
        return ['фасон', 'фактура', 'крой', 'тип цвета', 'материал'].includes(charName.toLowerCase());
    };

    const isMultiSelectTag = (charName: string): boolean => {
        return charName.toLowerCase() === 'крой' || charName.toLowerCase() === 'материал';
    };

    const isPrintTag = (charName: string): boolean => {
        return charName.toLowerCase() === 'принт';
    };

    const handleAddLayerTag = async () => {
        if (selectedLayers.length === 0) {
            toast.error('Выберите хотя бы один слой');
            return;
        }

        const layerValues = selectedLayers.join(', ');
        await saveLayerTag(layerValues);
    };

    const toggleLayer = (layerName: string) => {
        setSelectedLayers(prev => {
            if (prev.includes(layerName)) {
                return prev.filter(l => l !== layerName);
            } else {
                return [...prev, layerName];
            }
        });
    };

    const actionNewTag = async () => {
        if (nameNewTag === '') {
            toast.error('Заполните название тега');
            return;
        }

        const addTagAction = async () => {
            try {
                const currentClothId = isNew && onAddAction ? clothId : clothId;
                const finalValue = (nameNewTag.toLowerCase() === 'крой' || nameNewTag.toLowerCase() === 'материал') ?
                    normalizeMultiValue(valueNewTag) : valueNewTag;

                const newTag: CharsCloth = {
                    charId: generateIdNewTag(),
                    charValueId: null,
                    charName: nameNewTag,
                    charValueText: finalValue
                };

                const response = await clothesAxiosInstance.post<boolean>(
                    `editTag/${currentClothId}`,
                    newTag
                );

                if (response.data) {
                    toast.success('Тег успешно добавлен');
                    closeModalNewTag();
                    getCharsValues();
                } else {
                    toast.error('Ошибка при добавлении тега');
                }
            } catch (error) {
                toast.error('Ошибка при добавлении тега');
            }
        };

        if (isNew && onAddAction) {
            await onAddAction(addTagAction);
        } else {
            await addTagAction();
        }
    }

    const actionEditTag = async () => {
        if (!editingTag) return;

        try {
            const finalValue = ((editingTag.charName.toLowerCase() === 'крой' || editingTag.charName.toLowerCase() === 'материал') && editingTag.charValueText) ?
                normalizeMultiValue(editingTag.charValueText) : editingTag.charValueText;

            const updatedTag: CharsCloth = {
                charId: editingTag.charId,
                charValueId: editingTag.charValueId,
                charName: editingTag.charName,
                charValueText: finalValue || ''
            };

            const response = await clothesAxiosInstance.post<boolean>(
                `editTag/${clothId}`,
                updatedTag
            );

            if (response.data) {
                toast.success('Тег успешно обновлен');
                closeEditTag();
                setCharsShowingList(prev =>
                    prev.map(tag =>
                        tag.charId === editingTag.charId
                            ? { ...tag, charValueText: finalValue }
                            : tag
                    )
                );
            } else {
                toast.error('Ошибка при обновлении тега');
            }
        } catch (error) {
            toast.error('Ошибка при обновлении тега');
        }
    }

    const deletingCharValue = async (charValue: ICharsValuesCloth, e?: React.MouseEvent) => {
        if (e) {
            e.stopPropagation();
        }

        try {
            const previousValue = charValue.charValueText || '';

            const tagToUpdate: CharsCloth = {
                charId: charValue.charId,
                charValueId: charValue.charValueId,
                charName: charValue.charName,
                charValueText: ''
            };

            const response = await clothesAxiosInstance.post<boolean>(
                `editTag/${clothId}`,
                tagToUpdate
            );

            if (response.data) {
                toast.success('Тег успешно удален');

                saveLastChange(charValue.charId, previousValue, '');

                setCharsShowingList(prev =>
                    prev.map(tag =>
                        tag.charId === charValue.charId
                            ? { ...tag, charValueText: '' }
                            : tag
                    )
                );
            } else {
                toast.error('Ошибка при удалении тега');
            }
        } catch (error) {
            toast.error('Ошибка при удалении тега');
        }
    }

    const undoLastChange = async (charId: number, e?: React.MouseEvent) => {
        if (e) {
            e.stopPropagation();
        }

        if (!lastChangedTag || lastChangedTag.charId !== charId) return;

        const tagToRevert = charsShowingList.find(tag => tag.charId === charId);
        if (!tagToRevert) return;

        try {
            const revertedTag: CharsCloth = {
                charId: tagToRevert.charId,
                charValueId: tagToRevert.charValueId,
                charName: tagToRevert.charName,
                charValueText: lastChangedTag.previousValue
            };

            const response = await clothesAxiosInstance.post<boolean>(
                `editTag/${clothId}`,
                revertedTag
            );

            if (response.data) {
                toast.success('Изменение отменено');
                setLastChangedTag(null);
                setCharsShowingList(prev =>
                    prev.map(tag =>
                        tag.charId === charId
                            ? { ...tag, charValueText: lastChangedTag.previousValue }
                            : tag
                    )
                );
            } else {
                toast.error('Ошибка при отмене изменения');
            }
        } catch (error) {
            toast.error('Ошибка при отмене изменения');
        }
    }

    const handleSpecialTagChange = async (charValue: ICharsValuesCloth, newValue: string) => {
        const previousValue = charValue.charValueText || '';

        if (previousValue === newValue) return;

        try {
            const finalValue = (charValue.charName.toLowerCase() === 'крой' || charValue.charName.toLowerCase() === 'материал') ?
                normalizeMultiValue(newValue) : newValue;

            const updatedTag: CharsCloth = {
                charId: charValue.charId,
                charValueId: charValue.charValueId,
                charName: charValue.charName,
                charValueText: finalValue
            };

            const response = await clothesAxiosInstance.post<boolean>(
                `editTag/${clothId}`,
                updatedTag
            );

            if (response.data) {
                toast.success('Тег успешно обновлен');
            } else {
                toast.error('Ошибка при обновлении тега');
                setCharsShowingList(prev =>
                    prev.map(tag =>
                        tag.charId === charValue.charId
                            ? { ...tag, charValueText: previousValue }
                            : tag
                    )
                );
            }
        } catch (error) {
            toast.error('Ошибка при обновлении тега');
            setCharsShowingList(prev =>
                prev.map(tag =>
                    tag.charId === charValue.charId
                        ? { ...tag, charValueText: previousValue }
                        : tag
                )
            );
        }
    };

    const handleMultiSelectChange = (charValue: ICharsValuesCloth, item: string, e?: React.MouseEvent) => {
        if (e) {
            e.stopPropagation();
        }

        const currentValues = getSelectedMultiValues(charValue);
        let newValues: string[];

        if (currentValues.includes(item)) {
            newValues = currentValues.filter(value => value !== item);
        } else {
            newValues = [...currentValues, item];
        }

        const newValue = newValues.join(', ');

        setCharsShowingList(prev =>
            prev.map(tag =>
                tag.charId === charValue.charId
                    ? { ...tag, charValueText: newValue }
                    : tag
            )
        );

        handleSpecialTagChange(charValue, newValue);
    }

    const getSelectedMultiValues = (charValue: ICharsValuesCloth): string[] => {
        if (localSelectedMaterials[charValue.charId]) {
            return localSelectedMaterials[charValue.charId];
        }

        if (!charValue.charValueText) return [];
        return parseMultiValue(charValue.charValueText);
    };

    const toggleDropdown = (charId: number, e?: React.MouseEvent) => {
        if (e) {
            e.stopPropagation();
        }
        setDropdownOpen(prev => ({
            ...prev,
            [charId]: !prev[charId]
        }));
    };

    const closeAllDropdowns = () => {
        setDropdownOpen({});
        setLayerDropdownOpen({});
    };

    const setSolidColor = () => {
        if (editingTag) {
            setEditingTag({
                ...editingTag,
                charValueText: 'однотонный'
            });
        }
    };

    const handleRowClick = (charValue: ICharsValuesCloth, e: React.MouseEvent) => {
        const target = e.target as HTMLElement;
        if (target.closest(`.${s.dropdownContainer}`) ||
            target.closest(`.${s.modernRowActions}`) ||
            target.closest(`.${s.undoIconBtn}`)) {
            return;
        }

        const isMultiSelect = isMultiSelectTag(charValue.charName);
        const isSpecial = isSpecialTag(charValue.charName);

        if (!isMultiSelect && !isSpecial) {
            showModalEditTagValue(charValue);
        }
    };

    const openMaterialModal = (charValue: ICharsValuesCloth) => {
        setCurrentMaterialTag(charValue);
        setMaterialModalOpen(true);
        setMaterialSelectorKey(prev => prev + 1);

        if (!localSelectedMaterials[charValue.charId]) {
            const currentValues = getSelectedMultiValues(charValue);
            setLocalSelectedMaterials(prev => ({
                ...prev,
                [charValue.charId]: currentValues
            }));
        }
    };

    const closeMaterialModal = () => {
        setMaterialModalOpen(false);
        setTimeout(() => {
            setCurrentMaterialTag(null);
        }, 300);
    };

    const handleCloseMaterialModal = () => {
        setMaterialModalOpen(false);
        setCurrentMaterialTag(null);
    };

    const handleMaterialSelect = (selectedMaterials: string[]) => {
        if (currentMaterialTag) {
            setLocalSelectedMaterials(prev => ({
                ...prev,
                [currentMaterialTag.charId]: selectedMaterials
            }));

            const newValue = selectedMaterials.join(', ');

            setCharsShowingList(prev =>
                prev.map(tag =>
                    tag.charId === currentMaterialTag.charId
                        ? { ...tag, charValueText: newValue }
                        : tag
                )
            );

            handleSpecialTagChange(currentMaterialTag, newValue);
        }
    };

    const handleAddNewMaterial = async (materialName: string) => {
        await createMaterial(materialName);
    };

    const handleMaterialCancel = () => {
        if (currentMaterialTag) {
            const originalValues = currentMaterialTag.charValueText
                ? parseMultiValue(currentMaterialTag.charValueText)
                : [];

            setLocalSelectedMaterials(prev => ({
                ...prev,
                [currentMaterialTag.charId]: originalValues
            }));

            setCharsShowingList(prev =>
                prev.map(tag =>
                    tag.charId === currentMaterialTag.charId
                        ? { ...tag, charValueText: originalValues.join(', ') }
                        : tag
                )
            );
        }
        setMaterialModalOpen(false);
        setCurrentMaterialTag(null);
    };

    const stopPolling = () => {
        if (pollingInterval) {
            clearInterval(pollingInterval);
            setPollingInterval(null);
        }
    };

    useEffect(() => {
        getMaterials();
    }, []);

    useEffect(() => {
        const handleClickOutside = () => {
            closeAllDropdowns();
        };

        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (clothId && clothId !== "-1") {
            setTimeout(async () => {
                await getCharsValues();

            }, 2500);

        }
    }, [clothId]);

    useEffect(() => {
        return () => {
            if (pollingInterval) {
                clearInterval(pollingInterval);
            }
        };
    }, []);

    const isAddButtonDisabled = isNew && hasRequiredFields && !hasRequiredFields();

    return (
        <>
            <div className={s.modernTagsSection}>
                <div className={s.modernSectionHeader}>
                    <div className={s.modernTagActions}>
                        <button
                            className={s.modernBtnPrimary}
                            onClick={approvedSegmentation}
                        >
                            Сегментировано верно
                        </button>

                        <button
                            className={s.modernBtnPrimary}
                            onClick={startSegmentation}
                        >
                            Сегментация
                        </button>

                        <button
                            className={s.modernBtnPrimary}
                            onClick={() => showModalAddNewTag()}
                            disabled={isAddButtonDisabled}
                            title={isAddButtonDisabled ?
                                "Заполните обязательные поля для добавления тегов" : ""}
                        >
                            + Добавить вручную
                        </button>
                    </div>
                </div>

                {isTagsLoading ? (
                    <div className={s.loadingContainer}>
                        <Loader/>
                    </div>
                ) : charsShowingList.length ? (
                    <div className={s.modernTagsTable}>
                        <div className={s.modernTableHeader}>
                            <span>Характеристика</span>
                            <span>Значение</span>
                            <span>Значение, рассчитанное тегировщиком</span>
                            <span>Статус</span>
                            <span>Действия</span>
                        </div>

                        <div className={s.modernTableBody}>
                            {charsShowingList.map((charValue, index) => {
                                const isUserDefined = isUserDefinedTag(charValue.charName);
                                const isValid = isValidTag(charValue);
                                const isSpecial = isSpecialTag(charValue.charName);
                                const isMultiSelect = isMultiSelectTag(charValue.charName);
                                const isPrint = isPrintTag(charValue.charName);
                                const showValidation = !isUserDefined;
                                const selectedValues = getSelectedMultiValues(charValue);
                                const isOpen = dropdownOpen[charValue.charId] || false;
                                const isLayerOpen = layerDropdownOpen[charValue.charId] || false;

                                return (
                                    <div
                                        key={`char-${charValue.charId || index}`}
                                        className={`${s.modernTableRow} ${showValidation && !isValid ? s.invalid : ''} ${
                                            !isMultiSelect && !isSpecial ? s.clickableRow : ''
                                        }`}
                                        onClick={(e) => handleRowClick(charValue, e)}
                                    >
                                        <span className={s.tagHeader}>
                                            {charValue.charName}
                                            {isUserDefined && (
                                                <span className={s.modernCustomBadge}> (пользовательский)</span>
                                            )}
                                        </span>
                                        <span>
                                            {charValue.charName.toLowerCase() === 'материал' ? (
                                                <div className={s.dropdownContainer}>
                                                    <div
                                                        className={`${s.materialSelectorButton}`}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            openMaterialModal(charValue);
                                                        }}
                                                    >
                                                        <span className={s.dropdownPlaceholder}>
                                                            {selectedValues.length > 0
                                                                ? selectedValues.join(', ')
                                                                : 'Выберите материалы'
                                                            }
                                                        </span>
                                                        <span className={s.dropdownArrow}>▼</span>
                                                    </div>
                                                </div>
                                            ) : charValue.charName.toLowerCase() === 'крой' ? (
                                                <div className={s.dropdownContainer}>
                                                    <div
                                                        className={`${s.dropdownButton} ${isOpen ? s.open : ''}`}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            toggleDropdown(charValue.charId, e);
                                                        }}
                                                    >
                                                        <span className={s.dropdownPlaceholder}>
                                                            {selectedValues.length > 0
                                                                ? selectedValues.join(', ')
                                                                : 'Выберите варианты кроя'
                                                            }
                                                        </span>
                                                        <span className={s.dropdownArrow}>▼</span>
                                                    </div>
                                                    {isOpen && (
                                                        <div className={s.dropdownMenu}>
                                                            <div className={s.dropdownHeader}>
                                                                <span>Выберите варианты кроя</span>
                                                                <span className={s.selectedCount}>
                                                                    Выбрано: {selectedValues.length}
                                                                </span>
                                                            </div>
                                                            <div className={s.dropdownOptions}>
                                                                {cut.map(item => (
                                                                    <div
                                                                        key={item}
                                                                        className={`${s.dropdownOption} ${selectedValues.includes(item) ? s.selected : ''}`}
                                                                        onClick={(e) => handleMultiSelectChange(charValue, item, e)}
                                                                    >
                                                                        <span className={s.optionLabel}>{item}</span>
                                                                        {selectedValues.includes(item) && (
                                                                            <span className={s.checkmark}>✓</span>
                                                                        )}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            ) : isSpecial && !isMultiSelect ? (
                                                <select
                                                    value={charValue.charValueText || ''}
                                                    onChange={(e) => {
                                                        setCharsShowingList(prev =>
                                                            prev.map(tag =>
                                                                tag.charId === charValue.charId
                                                                    ? { ...tag, charValueText: e.target.value }
                                                                    : tag
                                                            )
                                                        );
                                                        handleSpecialTagChange(charValue, e.target.value);
                                                    }}
                                                    className={s.modernSelectSmall}
                                                >
                                                    <option value="" disabled>
                                                        Выберите {charValue.charName.toLowerCase()}
                                                    </option>
                                                    {charValue.charName.toLowerCase() === 'фасон' && style.map(item => (
                                                        <option key={item} value={item}>{item}</option>
                                                    ))}
                                                    {charValue.charName.toLowerCase() === 'фактура' && texture.map(item => (
                                                        <option key={item} value={item}>{item}</option>
                                                    ))}
                                                    {charValue.charName.toLowerCase() === 'тип цвета' && colorType.map(item => (
                                                        <option key={item} value={item}>{item}</option>
                                                    ))}
                                                </select>
                                            ) : charValue.charName.toLowerCase() === 'слой одежды' ? (
                                                <div className={s.dropdownContainer}>
                                                    <div
                                                        className={`${s.dropdownButton} ${isLayerOpen ? s.open : ''}`}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            toggleLayerDropdown(charValue.charId, e);
                                                        }}
                                                    >
                                                        <span className={s.dropdownPlaceholder}>
                                                            {getSelectedLayerValues(charValue).length > 0
                                                                ? getSelectedLayerValues(charValue).map(l => getLayerLocalizedName(l)).join(', ')
                                                                : 'Выберите слои'
                                                            }
                                                        </span>
                                                        <span className={s.dropdownArrow}>▼</span>
                                                    </div>
                                                    {isLayerOpen && (
                                                        <div className={s.dropdownMenu}>
                                                            <div className={s.dropdownHeader}>
                                                                <span>Выберите слои</span>
                                                                <span className={s.selectedCount}>
                                                                    Выбрано: {getSelectedLayerValues(charValue).length}
                                                                </span>
                                                            </div>
                                                            <div className={s.dropdownOptions}>
                                                                {LAYERS.map((layer) => (
                                                                    <div
                                                                        key={layer.name}
                                                                        className={`${s.dropdownOption} ${getSelectedLayerValues(charValue).includes(layer.name) ? s.selected : ''}`}
                                                                        onClick={(e) => handleLayerChange(charValue, layer.name, e)}
                                                                    >
                                                                        <span className={s.optionLabel}>{layer.localizedName}</span>
                                                                        {getSelectedLayerValues(charValue).includes(layer.name) && (
                                                                            <span className={s.checkmark}>✓</span>
                                                                        )}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                charValue.charValueText
                                            )}
                                        </span>
                                        <span className={s.calculateValue}>
                                            {charValue.calculateValue}
                                        </span>
                                        <span className={s.statusCell}>
                                            {showValidation && (
                                                <span className={isValid ? s.modernStatusValid : s.modernStatusInvalid}>
                                                    {isValid ? '✓' : '✗'}
                                                </span>
                                            )}
                                        </span>
                                        <div className={s.modernActionsContainer}>
                                            <div className={s.modernRowActions}>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        copyTagValue(charValue.charValueText);
                                                    }}
                                                    title="Копировать"
                                                >
                                                    <CopyIcon style={{fontSize: 16}}/>
                                                </button>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        deletingCharValue(charValue, e);
                                                    }}
                                                    title="Удалить"
                                                >
                                                    <DeleteIcon style={{fontSize: 16}}/>
                                                </button>
                                            </div>
                                            {canUndoTag(charValue.charId) && (
                                                <button
                                                    onClick={(e) => undoLastChange(charValue.charId, e)}
                                                    title="Отменить последнее изменение"
                                                    className={s.undoIconBtn}
                                                >
                                                    <ReturnIcon style={{width: '14px', height: '14px'}}/>
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ) : (
                    <div style={{textAlign: 'center', padding: '20px', color: '#999'}}>
                        Нет добавленных характеристик
                    </div>
                )}
            </div>

            <Modal
                show={materialModalOpen}
                onHide={closeMaterialModal}
                size="lg"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Выбор материалов</Modal.Title>
                </Modal.Header>
                <Modal.Body onClick={(e) => e.stopPropagation()}>
                    {currentMaterialTag && (
                        <MaterialSelector
                            key={`material-selector-${materialSelectorKey}-${currentMaterialTag.charId}`}
                            selectedMaterials={getSelectedMultiValues(currentMaterialTag)}
                            onSelectMaterials={handleMaterialSelect}
                            availableMaterials={material}
                            onAddNewMaterial={handleAddNewMaterial}
                        />
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        className={s.btnCancel}
                        onClick={handleMaterialCancel}
                    >
                        Отмена
                    </Button>
                    <Button
                        className={s.btnSecondary}
                        onClick={closeMaterialModal}
                    >
                        Закрыть
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={requiredAddNewTag} onHide={closeModalNewTag}>
                <Modal.Header closeButton>
                    <Modal.Title>Новый тег</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className={s.modernFormGroup}>
                        <Form.Label>Название</Form.Label>
                        <Form.Control
                            type="text"
                            value={nameNewTag}
                            onChange={(e) => setNameNewTag(e.target.value)}
                            placeholder="Введите название характеристики"
                        />
                    </div>
                    <div className={s.modernFormGroup}>
                        <Form.Label>Значение</Form.Label>
                        {nameNewTag.toLowerCase() === 'крой' || nameNewTag.toLowerCase() === 'материал' ? (
                            <div className={s.dropdownContainer}>
                                <div
                                    className={`${s.dropdownButton} ${s.modalDropdown}`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        const dropdownId = nameNewTag.toLowerCase() === 'крой'
                                            ? MODAL_DROPDOWN_ID
                                            : MATERIAL_MODAL_DROPDOWN_ID;
                                        setDropdownOpen(prev => ({
                                            ...prev,
                                            [dropdownId]: !prev[dropdownId]
                                        }));
                                    }}
                                >
                                    <span className={s.dropdownPlaceholder}>
                                        {parseMultiValue(valueNewTag).length > 0
                                            ? parseMultiValue(valueNewTag).join(', ')
                                            : nameNewTag.toLowerCase() === 'крой'
                                                ? 'Выберите варианты кроя'
                                                : 'Выберите материалы'
                                        }
                                    </span>
                                    <span className={s.dropdownArrow}>▼</span>
                                </div>
                                {dropdownOpen[nameNewTag.toLowerCase() === 'крой' ? MODAL_DROPDOWN_ID : MATERIAL_MODAL_DROPDOWN_ID] && (
                                    <div className={`${s.dropdownMenu} ${s.modalDropdownMenu}`}>
                                        <div className={s.dropdownHeader}>
                                            <span>
                                                {nameNewTag.toLowerCase() === 'крой'
                                                    ? 'Выберите варианты кроя'
                                                    : 'Выберите материалы'
                                                }
                                            </span>
                                            <span className={s.selectedCount}>
                                                Выбрано: {parseMultiValue(valueNewTag).length}
                                            </span>
                                        </div>
                                        <div className={s.dropdownOptions}>
                                            {(nameNewTag.toLowerCase() === 'крой' ? cut : material).map(item => (
                                                <label key={item} className={s.dropdownOption}>
                                                    <input
                                                        type="checkbox"
                                                        checked={parseMultiValue(valueNewTag).includes(item)}
                                                        onChange={(e) => {
                                                            const currentValues = parseMultiValue(valueNewTag);
                                                            let newValues: string[];
                                                            if (e.target.checked) {
                                                                newValues = [...currentValues, item];
                                                            } else {
                                                                newValues = currentValues.filter(value => value !== item);
                                                            }
                                                            setValueNewTag(newValues.join(', '));
                                                        }}
                                                        className={s.optionCheckbox}
                                                    />
                                                    <span className={s.optionLabel}>{item}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : ['фасон', 'фактура', 'тип цвета'].includes(nameNewTag.toLowerCase()) ? (
                            <Form.Select
                                value={valueNewTag}
                                onChange={(e) => setValueNewTag(e.target.value)}
                            >
                                <option value="" disabled>
                                    Выберите {nameNewTag.toLowerCase()}
                                </option>
                                {nameNewTag.toLowerCase() === 'фасон' && style.map(item => (
                                    <option key={item} value={item}>{item}</option>
                                ))}
                                {nameNewTag.toLowerCase() === 'фактура' && texture.map(item => (
                                    <option key={item} value={item}>{item}</option>
                                ))}
                                {nameNewTag.toLowerCase() === 'тип цвета' && colorType.map(item => (
                                    <option key={item} value={item}>{item}</option>
                                ))}
                            </Form.Select>
                        ) : (
                            <Form.Control
                                type="text"
                                value={valueNewTag}
                                onChange={(e) => setValueNewTag(e.target.value)}
                                placeholder="Введите значение характеристики"
                            />
                        )}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        className={s.btnCancel}
                        onClick={closeModalNewTag}
                    >
                        Отмена
                    </Button>
                    <Button
                        className={s.btnPrimary}
                        onClick={actionNewTag}
                        disabled={!nameNewTag.trim()}
                    >
                        Добавить тег
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={requiredEditTag} onHide={closeEditTag}>
                <Modal.Header closeButton>
                    <Modal.Title>Редактировать тег</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {editingTag && (
                        <form onSubmit={(e) => {
                            e.preventDefault();
                            if (editingTag?.charName?.trim()) {
                                actionEditTag();
                            }
                        }}>
                            <div className={s.modernFormGroup}>
                                <Form.Label>Название</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={editingTag.charName}
                                    onChange={(e) => setEditingTag({
                                        ...editingTag,
                                        charName: e.target.value
                                    })}
                                    disabled={!isUserDefinedTag(editingTag.charName)}
                                />
                                {!isUserDefinedTag(editingTag.charName) && (
                                    <small className={s.helpText}>
                                        Системные теги нельзя редактировать
                                    </small>
                                )}
                            </div>
                            <div className={s.modernFormGroup}>
                                <Form.Label>Значение</Form.Label>
                                {editingTag.charName.toLowerCase() === 'крой' || editingTag.charName.toLowerCase() === 'материал' ? (
                                    <div className={s.dropdownContainer}>
                                        <div
                                            className={`${s.dropdownButton} ${s.modalDropdown}`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                const dropdownId = editingTag.charName.toLowerCase() === 'крой'
                                                    ? EDIT_MODAL_DROPDOWN_ID
                                                    : MATERIAL_EDIT_MODAL_DROPDOWN_ID;
                                                setDropdownOpen(prev => ({
                                                    ...prev,
                                                    [dropdownId]: !prev[dropdownId]
                                                }));
                                            }}
                                        >
                                            <span className={s.dropdownPlaceholder}>
                                                {parseMultiValue(editingTag.charValueText || '').length > 0
                                                    ? parseMultiValue(editingTag.charValueText || '').join(', ')
                                                    : editingTag.charName.toLowerCase() === 'крой'
                                                        ? 'Выберите варианты кроя'
                                                        : 'Выберите материалы'
                                                }
                                            </span>
                                            <span className={s.dropdownArrow}>▼</span>
                                        </div>
                                        {dropdownOpen[editingTag.charName.toLowerCase() === 'крой' ? EDIT_MODAL_DROPDOWN_ID : MATERIAL_EDIT_MODAL_DROPDOWN_ID] && (
                                            <div className={`${s.dropdownMenu} ${s.modalDropdownMenu}`}>
                                                <div className={s.dropdownHeader}>
                                                    <span>
                                                        {editingTag.charName.toLowerCase() === 'крой'
                                                            ? 'Выберите варианты кроя'
                                                            : 'Выберите материалы'
                                                        }
                                                    </span>
                                                    <span className={s.selectedCount}>
                                                        Выбрано: {parseMultiValue(editingTag.charValueText || '').length}
                                                    </span>
                                                </div>
                                                <div className={s.dropdownOptions}>
                                                    {(editingTag.charName.toLowerCase() === 'крой' ? cut : material).map(item => (
                                                        <label key={item} className={s.dropdownOption}>
                                                            <input
                                                                type="checkbox"
                                                                checked={parseMultiValue(editingTag.charValueText || '').includes(item)}
                                                                onChange={(e) => {
                                                                    const currentValues = parseMultiValue(editingTag.charValueText || '');
                                                                    let newValues: string[];
                                                                    if (e.target.checked) {
                                                                        newValues = [...currentValues, item];
                                                                    } else {
                                                                        newValues = currentValues.filter(value => value !== item);
                                                                    }
                                                                    setEditingTag({
                                                                        ...editingTag,
                                                                        charValueText: newValues.join(', ')
                                                                    });
                                                                }}
                                                                className={s.optionCheckbox}
                                                            />
                                                            <span className={s.optionLabel}>{item}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ) : isSpecialTag(editingTag.charName) && !isMultiSelectTag(editingTag.charName) ? (
                                    <Form.Select
                                        value={editingTag.charValueText || ''}
                                        onChange={(e) => setEditingTag({
                                            ...editingTag,
                                            charValueText: e.target.value
                                        })}
                                    >
                                        <option value="" disabled>
                                            Выберите {editingTag.charName.toLowerCase()}
                                        </option>
                                        {editingTag.charName.toLowerCase() === 'фасон' && style.map(item => (
                                            <option key={item} value={item}>{item}</option>
                                        ))}
                                        {editingTag.charName.toLowerCase() === 'фактура' && texture.map(item => (
                                            <option key={item} value={item}>{item}</option>
                                        ))}
                                        {editingTag.charName.toLowerCase() === 'тип цвета' && colorType.map(item => (
                                            <option key={item} value={item}>{item}</option>
                                        ))}
                                    </Form.Select>
                                ) : (
                                    <div className={s.inputWithButton}>
                                        <Form.Control
                                            type="text"
                                            value={editingTag.charValueText || ''}
                                            onChange={(e) => setEditingTag({
                                                ...editingTag,
                                                charValueText: e.target.value
                                            })}
                                            placeholder="Введите значение характеристики"
                                        />
                                        {isPrintTag(editingTag.charName) && (
                                            <button
                                                type="button"
                                                className={s.solidColorBtn}
                                                onClick={setSolidColor}
                                                title="Выбрать 'однотонный'"
                                            >
                                                Однотонный
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                            <button type="submit" style={{ display: 'none' }} />
                        </form>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        onClick={closeEditTag}
                        className={s.btnCancel}
                    >
                        Отмена
                    </Button>
                    <Button
                        onClick={actionEditTag}
                        className={s.btnPrimary}
                        disabled={!editingTag?.charName?.trim()}
                    >
                        Сохранить изменения
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    )
}